"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Maximize2, X } from "lucide-react";

/** Shared by Writer and Editor screenshots; native dialogs handle focus and Escape. */
export function MarketingImageLightbox({ children, enlargedImage, title, enlargeLabel, closeLabel }: {
  children: ReactNode;
  enlargedImage: ReactNode;
  title: string;
  enlargeLabel: string;
  closeLabel: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.showModal();
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  return (
    <>
      <button
        type="button"
        data-screenshot-open
        onClick={() => setOpen(true)}
        aria-label={`${enlargeLabel}: ${title}`}
        className="group relative block w-full cursor-zoom-in overflow-hidden rounded-xl border border-border bg-muted/20 p-1.5 text-start shadow-sm transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
      >
        {children}
        <span className="absolute end-3 bottom-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-background/95 px-2 py-1 text-xs text-foreground shadow-sm">
          <Maximize2 className="h-3 w-3" aria-hidden="true" />{enlargeLabel}
        </span>
      </button>
      <dialog
        ref={dialog}
        data-screenshot-dialog
        aria-label={title}
        onClose={() => setOpen(false)}
        onClick={(event) => { if (event.target === event.currentTarget) dialog.current?.close(); }}
        className="m-auto max-h-[95dvh] w-[calc(100%-1.5rem)] max-w-[1440px] overflow-hidden rounded-xl border border-border bg-background p-0 text-foreground shadow-2xl backdrop:bg-black/80"
      >
        {open && <>
          <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-2">
            <p className="text-sm font-medium">{title}</p>
            <button type="button" data-screenshot-close onClick={() => dialog.current?.close()} aria-label={closeLabel} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-muted focus-visible:outline-2 focus-visible:outline-accent">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="max-h-[80dvh] overflow-auto" tabIndex={0} role="region" aria-label={title}>{enlargedImage}</div>
        </>}
      </dialog>
    </>
  );
}
